import type { ReactNode } from 'react';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';

/**
 * Peças comuns às abas da tela de Sincronização: a situação no ERP, as duas
 * datas de cada registro e o código do produto.
 */

/**
 * O rótulo de quem está fora de uso (`ativo = false`).
 *
 * Com `excluidoEm`, o ERP excluiu; com `excluidoEm` nulo, o registro existe lá
 * mas está inativo. O saldo não tem `excluidoEm` (prop ausente): a API só diz
 * que está fora de uso, e a tela não inventa qual dos dois.
 */
function rotuloDoForaDeUso(excluidoEm: string | null | undefined, feminino: boolean): string {
  if (excluidoEm === undefined) {
    return 'Fora de uso no ERP';
  }
  if (excluidoEm === null) {
    return feminino ? 'Inativa no ERP' : 'Inativo no ERP';
  }
  return feminino ? 'Excluída no ERP' : 'Excluído no ERP';
}

/**
 * Ativo, inativo ou excluído no ERP. Nada se apaga aqui: o registro continua,
 * marcado, com a data em que a sincronização viu a exclusão.
 */
export function SituacaoNoErp({
  ativo,
  excluidoEm,
  feminino = false,
  children,
}: {
  readonly ativo: boolean;
  /** Ausente quando o registro não tem a coluna (saldo). */
  readonly excluidoEm?: string | null;
  readonly feminino?: boolean;
  readonly children?: ReactNode;
}) {
  return (
    <span className="sincronizacao__pilha">
      {ativo ? (
        <ChipStatus tom="concluida" rotulo={feminino ? 'Ativa' : 'Ativo'} tamanho="tabela" />
      ) : (
        <ChipStatus
          tom="desativado"
          rotulo={rotuloDoForaDeUso(excluidoEm, feminino)}
          tamanho="tabela"
        />
      )}
      {!ativo && excluidoEm !== null && excluidoEm !== undefined && (
        <span className="sincronizacao__discreto">em {formatarDataHora(excluidoEm)}</span>
      )}
      {children}
    </span>
  );
}

/**
 * "Atualizado em" é quando o registro mudou de valor aqui; "sincronizado em",
 * a última leitura do ERP que passou por ele — mesmo sem mudar nada.
 */
export function DatasDaSincronizacao({
  atualizadoEm,
  sincronizadoEm,
}: {
  readonly atualizadoEm: string;
  readonly sincronizadoEm: string;
}) {
  return (
    <dl className="sincronizacao__datas">
      <div>
        <dt>Atualizado em</dt>
        <dd>{formatarDataHora(atualizadoEm)}</dd>
      </div>
      <div>
        <dt>Sincronizado em</dt>
        <dd>{formatarDataHora(sincronizadoEm)}</dd>
      </div>
    </dl>
  );
}

/** Código em cima (o que se confere com o ERP), descrição embaixo. */
export function CodigoDoProduto({
  codPro,
  codDer,
  descricaoDerivacao = null,
  nome,
}: {
  readonly codPro: string;
  readonly codDer: string;
  readonly descricaoDerivacao?: string | null;
  readonly nome: string;
}) {
  return (
    <span className="sincronizacao__pilha">
      <span className="sincronizacao__codigo">
        {codPro}
        {codDer !== '' && (
          <span className="sincronizacao__derivacao">
            {' '}
            {codDer}
            {descricaoDerivacao !== null && ` · ${descricaoDerivacao}`}
          </span>
        )}
      </span>
      <span className="sincronizacao__discreto">{nome}</span>
    </span>
  );
}
